import React from "react";
import { createBrowserRouter, redirect } from "react-router-dom";
import { AuthContextProvider } from "./context/auth-context";
import ErrorBoundary from "./pages/ErrorBoundary";
import HomePage from "./pages/HomePage";
import RootLayout from "./pages/Root";
import WithNavigationLayout from "./pages/WithNavigationLayout";
import { loader as homePageLoader } from "./pages/loaders/HomePageLoader";
import { loader as layoutLoader } from "./pages/loaders/NavigationLayoutLoader";
import { auth } from "./services/firebase";

const withAuthLoader = (loader) => {
  return async (args) => {
    const route = new URL(args.request.url);
    const path = route.pathname;
    const user = await new Promise((resolve, reject) => {
      const unsubscribe = auth.onAuthStateChanged((user) => {
        unsubscribe();
        resolve(user);
      });
    });
    if (!user) return redirect("/?login=true&from=" + path);

    if (args) args.user = user;
    return loader(args);
  };
};

const router = createBrowserRouter([
  {
    element: <AuthContextProvider />,
    children: [
      {
        path: "/",
        element: <RootLayout />,
        errorElement: <ErrorBoundary />,
        loader: async () => {
          // Check if localStorage and sessionStorage are available
          const storageAvailable = (type) => {
            let storage;
            try {
              storage = window[type];
              const test = "__storage_test__";
              storage.setItem(test, test);
              storage.removeItem(test);
              return true;
            } catch (e) {
              return false;
            }
          };

          const isLocalStorageAvailable = storageAvailable("localStorage");
          const isSessionStorageAvailable = storageAvailable("sessionStorage");

          return { isLocalStorageAvailable, isSessionStorageAvailable };
        },
        children: [
          {
            element: <WithNavigationLayout />,
            loader: layoutLoader,
            children: [
              { index: true, element: <HomePage />, loader: homePageLoader },
              {
                //charter flights page
                path: "charter-flights",
                lazy: () => import("./pages/CharterFlights"),
              },
              {
                path: "cheap-flights",
                shouldRevalidate: ({ nextUrl }) => nextUrl.pathname === "/cheap-flights",
                lazy: () => import("./pages/CheapFlightsLayout"),
                async loader({ request, params }) {
                  let { loader } = await import("./pages/loaders/CheapFlightsLoader");
                  return loader({ request, params });
                },
                children: [
                  {
                    path: ":type/:origin/*",
                    lazy: () => import("./pages/CheapFlights"),
                    async loader({ request, params }) {
                      let { loader } = await import("./pages/loaders/CheapFlightDealsLoader");
                      return loader({ request, params });
                    },
                  },
                ],
              },
              {
                path: "business",
                lazy: () => import("./pages/BusinessBooking"),
                children: [
                  { path: "sent", lazy: () => import("./pages/GroupBookingRequestSent") },
                ],
              },
              {
                path: "groups",
                lazy: () => import("./pages/GroupBooking"),
                children: [
                  { path: "sent", lazy: () => import("./pages/GroupBookingRequestSent") },
                ],
              },
              {
                path: "search/results",
                lazy: () => import("./pages/SearchResults"),
                async loader({ request, params }) {
                  let { loader } = await import("./pages/loaders/SearchResultsLoader");
                  return loader({ request, params });
                },
              },
              {
                path: "dashboard",
                lazy: () => import("./pages/DashboardLayout"),
                async loader({ request, params }) {
                  let { loader } = await import("./pages/loaders/DashboardLoader");
                  return withAuthLoader(loader)({ request, params });
                },
                children: [
                  {
                    path: "trips",
                    lazy: () => import("./pages/dashboard/UserTrips.jsx"),
                    async loader({ request, params }) {
                      let { loader } = await import("./pages/loaders/UserTripsLoader");
                      return withAuthLoader(loader)({ request, params });
                    },
                  },
                  {
                    path: "trips/:orderId",
                    lazy: () => import("./pages/dashboard/UserTripDetails"),
                    async loader({ request, params }) {
                      let { loader } = await import("./pages/loaders/UserTripDetailsLoader");
                      return withAuthLoader(loader)({ request, params });
                    },
                  },
                  { path: "passengers", lazy: () => import("./pages/TempUnavailable") },
                  {
                    path: "invoices",
                    lazy: () => import("./pages/dashboard/UserInvoices"),
                    async loader({ request, params }) {
                      let { loader } = await import("./pages/loaders/UserInvoicesLoader");
                      return withAuthLoader(loader)({ request, params });
                    },
                  },
                  { path: "alerts", lazy: () => import("./pages/TempUnavailable") },
                  { path: "referral", lazy: () => import("./pages/TempUnavailable") },
                  { path: "preferences", lazy: () => import("./pages/TempUnavailable") },
                  { path: "support", lazy: () => import("./pages/TempUnavailable") },
                ],
              },
              {
                path: "info/:id",
                lazy: () => import("./pages/InfoPageLayout"),
                async loader({ request, params }) {
                  let { loader } = await import("./pages/loaders/InfoPageLoader");
                  return loader({ request, params });
                },
              },
              {
                path: "info/faq",
                lazy: () => import("./pages/FAQPage"),
                async loader({ request, params }) {
                  let { loader } = await import("./pages/loaders/FAQPageLoader");
                  return loader({ request, params });
                },
              },
              {
                path: "info/faq-topics",
                lazy: () => import("./pages/FAQTopicsPage"),
                async loader({ request, params }) {
                  let { loader } = await import("./pages/loaders/FAQTopicsLoader");
                  return loader({ request, params });
                },
              },
              {
                path: "about-us",
                lazy: () => import("./pages/InfoPageLayout"),
                async loader({ request, params }) {
                  let { loader } = await import("./pages/loaders/InfoPageLoader");
                  return loader({ request, params });
                },
              },
              { path: "*", lazy: () => import("./pages/PageNotFound") },
            ],
          },
          { path: "booking/cancelled", lazy: () => import("./pages/BookingCancelled") },
          {
            id: "bookingRouteController",
            path: "booking",
            lazy: () =>
              import("./context/booking-flow-context").then((m) => ({
                element: <m.default />,
              })),
            async loader({ request, params }) {
              let { loader } = await import("./pages/loaders/BookingPageLoader");
              return loader({ request, params });
            },
            children: [
              {
                lazy: () => import("./pages/BookingLayoutWrapper"),
                children: [
                  { path: ":bookingStep", lazy: () => import("./pages/BookingFlowPage") },
                ],
              },
            ],
          },
          {
            path: "order",
            children: [
              {
                path: ":orderId/thankyou",
                lazy: () => import("./pages/OrderComplete"),
                async loader({ request, params }) {
                  let { loader } = await import("./pages/loaders/OrderCompleteLoader");
                  return loader({ request, params });
                },
              },
              { path: ":orderId/error", lazy: () => import("./pages/OrderError") },
              {
                path: ":orderId/confirmation",
                lazy: () => import("./pages/OfflineOrderConfirmed"),
                async loader({ request, params }) {
                  let { loader } = await import("./pages/loaders/OrderReceivedLoader");
                  return loader({ request, params });
                },
              },
              { path: ":orderId/complete", element: <div>this order has been paid</div> },
            ],
          },
        ],
      },
    ],
  },
]);

export default router;